import { Fragment, useEffect, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  BarChart3,
  Building2,
  Calendar,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Eye,
  Network,
  Star,
  Stethoscope,
  TrendingUp,
  Users,
} from "lucide-react";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import { PageHeader } from "@/components/layout/page-header";
import { buttonVariants } from "@/components/ui/button-variants";
import { LoadErrorState } from "@/components/ui/load-error-state";
import { StatCard } from "@/components/ui/stat-card";

/**
 * Network dashboard — for users who own/direct multiple facilities.
 *
 * 1:1 port of legacy src/components/account/network-dashboard-client.tsx +
 * the SSR chrome/redirects from
 * src/app/[locale]/[country]/account/network/page.tsx:
 *   - 0 facilities → redirect to /account (workspace resolver takes over)
 *   - 1 facility  → redirect to /account/facility/{id}/dashboard
 *   - otherwise   → KPI row, sortable per-facility table, month bar chart,
 *     quick-link cards.
 *
 * Data: GET /api/account/network/dashboard (apps/api account-misc.ts). The
 * facility count for the redirect decision comes from the same response —
 * no extra pre-check request.
 */

interface NetworkFacilityStat {
  id: string;
  name: string;
  appointmentsToday: number;
  appointmentsMonth: number;
  providerCount: number;
  staffCount: number;
  rating: number | null;
  profileViews: number;
  last7Days: number[];
}

interface NetworkDashboardData {
  facilities: NetworkFacilityStat[];
  totalAppointmentsToday: number;
  totalAppointmentsMonth: number;
  totalProviders: number;
  totalStaff: number;
  avgRating: number | null;
}

export const Route = createFileRoute("/_authenticated/account/network")({
  component: NetworkPage,
});

// Rating scale tones, from the brand tokens.
function RatingBadge({ rating }: { rating: number | null }) {
  const { i18n } = useLingui();
  if (rating === null) return <span className="text-muted-foreground text-xs">—</span>;
  const color =
    rating >= 4.5
      ? "text-success-strong"
      : rating >= 4.0
        ? "text-primary"
        : rating >= 3.5
          ? "text-warning-strong"
          : "text-destructive";
  return (
    <span className={cn("flex items-center gap-1 text-sm font-semibold", color)}>
      <Star className="size-3.5 fill-current" aria-hidden />
      {rating.toLocaleString(i18n.locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
    </span>
  );
}

function MiniBarChart({ data }: { data: number[] }) {
  const max = Math.max(...data, 1);
  return (
    <div className="flex h-8 items-end gap-0.5">
      {data.map((val, i) => {
        const height = Math.round((val / max) * 100);
        const isToday = i === data.length - 1;
        return (
          <div
            key={i}
            className={cn(
              "min-h-[2px] w-3 rounded-sm transition-all",
              isToday ? "bg-primary" : "bg-primary/30",
            )}
            style={{ height: `${Math.max(height, 4)}%` }}
            title={`${val}`}
          />
        );
      })}
    </div>
  );
}

function SkeletonRow() {
  return (
    <tr>
      {[1, 2, 3, 4, 5, 6, 7].map((i) => (
        <td key={i} className="px-4 py-3">
          <div className="bg-muted h-4 animate-pulse rounded" />
        </td>
      ))}
    </tr>
  );
}

type SortKey = "name" | "today" | "month" | "providers" | "rating" | "views";

function SortHeader({
  col,
  label,
  sortKey,
  sortAsc,
  onSort,
}: {
  col: SortKey;
  label: string;
  sortKey: SortKey;
  sortAsc: boolean;
  onSort: (key: SortKey) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSort(col)}
      className={cn(
        "inline-flex min-h-11 items-center gap-1 text-xs font-semibold tracking-wide uppercase transition-colors",
        sortKey === col ? "text-primary" : "text-muted-foreground hover:text-foreground",
      )}
    >
      {label}
      {sortKey === col &&
        (sortAsc ? (
          <ChevronUp className="size-3.5" aria-hidden />
        ) : (
          <ChevronDown className="size-3.5" aria-hidden />
        ))}
    </button>
  );
}

function NetworkPage() {
  const { t, i18n } = useLingui();
  const navigate = useNavigate();

  const [sortKey, setSortKey] = useState<SortKey>("today");
  const [sortAsc, setSortAsc] = useState(false);
  // Mobile row detail (P25): the columns hidden below lg open as a row
  // underneath, so month, doctors, views and the 7-day trend stay reachable.
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["api", "/api/account/network/dashboard"],
    queryFn: () => apiFetch.get<NetworkDashboardData>("/api/account/network/dashboard"),
  });
  const data = query.data;
  const loading = query.isPending;

  // Legacy SSR redirects (network/page.tsx:33-40): 1 facility → its
  // dashboard, 0 facilities → account root (workspace resolver).
  const facilityCount = data ? data.facilities.length : null;
  const singleFacilityId = facilityCount === 1 ? data!.facilities[0].id : null;
  useEffect(() => {
    if (facilityCount === null) return;
    if (singleFacilityId) {
      navigate({
        to: "/account/facility/$facilityId/dashboard",
        params: { facilityId: singleFacilityId },
        replace: true,
      });
    } else if (facilityCount === 0) {
      navigate({ to: "/account", replace: true });
    }
  }, [facilityCount, singleFacilityId, navigate]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortAsc((v) => !v);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
  };

  const sortedFacilities = data
    ? [...data.facilities].sort((a, b) => {
        let diff = 0;
        switch (sortKey) {
          case "name":
            diff = a.name.localeCompare(b.name);
            break;
          case "today":
            diff = a.appointmentsToday - b.appointmentsToday;
            break;
          case "month":
            diff = a.appointmentsMonth - b.appointmentsMonth;
            break;
          case "providers":
            diff = a.providerCount - b.providerCount;
            break;
          case "rating":
            diff = (a.rating ?? 0) - (b.rating ?? 0);
            break;
          case "views":
            diff = a.profileViews - b.profileViews;
            break;
        }
        return sortAsc ? diff : -diff;
      })
    : [];

  const ariaSort = (col: SortKey): "ascending" | "descending" | undefined =>
    sortKey === col ? (sortAsc ? "ascending" : "descending") : undefined;

  const header = (
    <PageHeader
      leading={
        <span className="bg-primary-soft flex size-10 shrink-0 items-center justify-center rounded-xl">
          <Network className="text-primary size-5" aria-hidden />
        </span>
      }
      title={<Trans>Clinic Network</Trans>}
      description={
        data ? (
          <Trans>{data.facilities.length} facilities</Trans>
        ) : loading ? (
          <span className="bg-muted inline-block h-4 w-24 animate-pulse rounded align-middle" />
        ) : undefined
      }
    />
  );

  // P16: a failed load is an error, not a network with zero appointments.
  if (query.isError && !data) {
    return (
      <div className="space-y-6">
        {header}
        <LoadErrorState
          title={<Trans>Could not load your clinic network.</Trans>}
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
        />
        <AccountSecurityBanner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {header}

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatCard
          label={<Trans>Appointments today</Trans>}
          value={data?.totalAppointmentsToday ?? 0}
          icon={Calendar}
          loading={loading}
        />
        <StatCard
          label={<Trans>Appointments this month</Trans>}
          value={data?.totalAppointmentsMonth ?? 0}
          icon={TrendingUp}
          loading={loading}
        />
        <StatCard
          label={t`Total Doctors`}
          value={data?.totalProviders ?? 0}
          icon={Stethoscope}
          tone="accent"
          loading={loading}
        />
        <StatCard
          label={t`Total Staff`}
          value={data?.totalStaff ?? 0}
          icon={Users}
          tone="accent"
          loading={loading}
        />
        <StatCard
          label={t`Avg. Rating`}
          value={data?.avgRating ?? "—"}
          format={{ minimumFractionDigits: 1, maximumFractionDigits: 1 }}
          icon={Star}
          tone="warning"
          loading={loading}
        />
      </div>

      {/* Per-facility comparison table */}
      <div>
        <h2 className="text-foreground mb-3 text-sm font-semibold">
          <Trans>Per Location</Trans>
        </h2>
        <div className="border-border overflow-x-auto rounded-xl border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-border border-b">
                <th className="px-4 py-1 text-left" aria-sort={ariaSort("name")}>
                  <SortHeader
                    col="name"
                    label={t`Location`}
                    sortKey={sortKey}
                    sortAsc={sortAsc}
                    onSort={handleSort}
                  />
                </th>
                <th className="px-4 py-1 text-right" aria-sort={ariaSort("today")}>
                  <SortHeader
                    col="today"
                    label={t`Today`}
                    sortKey={sortKey}
                    sortAsc={sortAsc}
                    onSort={handleSort}
                  />
                </th>
                <th
                  className="hidden px-4 py-1 text-right md:table-cell"
                  aria-sort={ariaSort("month")}
                >
                  <SortHeader
                    col="month"
                    label={t`This Month`}
                    sortKey={sortKey}
                    sortAsc={sortAsc}
                    onSort={handleSort}
                  />
                </th>
                <th
                  className="hidden px-4 py-1 text-right sm:table-cell"
                  aria-sort={ariaSort("providers")}
                >
                  <SortHeader
                    col="providers"
                    label={t`Doctors`}
                    sortKey={sortKey}
                    sortAsc={sortAsc}
                    onSort={handleSort}
                  />
                </th>
                <th className="px-4 py-1 text-right" aria-sort={ariaSort("rating")}>
                  <SortHeader
                    col="rating"
                    label={t`Rating`}
                    sortKey={sortKey}
                    sortAsc={sortAsc}
                    onSort={handleSort}
                  />
                </th>
                <th
                  className="hidden px-4 py-1 text-right lg:table-cell"
                  aria-sort={ariaSort("views")}
                >
                  <SortHeader
                    col="views"
                    label={t`Views`}
                    sortKey={sortKey}
                    sortAsc={sortAsc}
                    onSort={handleSort}
                  />
                </th>
                <th className="hidden px-4 py-1 text-right md:table-cell">
                  <span className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                    <Trans>7 days</Trans>
                  </span>
                </th>
                <th className="px-2 py-1">
                  <span className="sr-only">
                    <Trans>Open dashboard</Trans>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <>
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                </>
              ) : sortedFacilities.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-muted-foreground px-4 py-8 text-center text-sm">
                    <Trans>No clinic network. Add at least 2 clinics to use this feature.</Trans>
                  </td>
                </tr>
              ) : (
                sortedFacilities.map((loc) => {
                  const isExpanded = expandedId === loc.id;
                  const detailId = `network-row-${loc.id}`;
                  const facilityName = loc.name;
                  return (
                    <Fragment key={loc.id}>
                      <tr
                        className={cn(
                          "border-border hover:bg-muted/50 border-b transition-colors",
                          !isExpanded && "last:border-0",
                        )}
                      >
                        {/* Facility name — toggles the row detail below lg */}
                        <td className="px-4 py-2">
                          <button
                            type="button"
                            onClick={() => setExpandedId(isExpanded ? null : loc.id)}
                            aria-expanded={isExpanded}
                            aria-controls={detailId}
                            className="flex min-h-11 w-full items-center gap-2 text-left lg:pointer-events-none"
                          >
                            <span className="bg-accent-soft flex size-7 shrink-0 items-center justify-center rounded-lg">
                              <Building2 className="text-accent-strong size-3.5" aria-hidden />
                            </span>
                            <span className="text-foreground font-medium">{facilityName}</span>
                            <ChevronDown
                              className={cn(
                                "text-muted-foreground ml-auto size-4 shrink-0 transition-transform lg:hidden",
                                isExpanded && "rotate-180",
                              )}
                              aria-hidden
                            />
                          </button>
                        </td>
                        {/* Today */}
                        <td className="px-4 py-2 text-right">
                          <span
                            className={cn(
                              "text-sm font-semibold",
                              loc.appointmentsToday > 0
                                ? "text-foreground"
                                : "text-muted-foreground",
                            )}
                          >
                            {loc.appointmentsToday}
                          </span>
                        </td>
                        {/* This month */}
                        <td className="hidden px-4 py-2 text-right md:table-cell">
                          <span className="text-foreground text-sm font-medium">
                            {loc.appointmentsMonth}
                          </span>
                        </td>
                        {/* Providers */}
                        <td className="hidden px-4 py-2 text-right sm:table-cell">
                          <div className="flex items-center justify-end gap-1">
                            <Stethoscope className="text-muted-foreground size-3.5" aria-hidden />
                            <span className="text-foreground text-sm">{loc.providerCount}</span>
                          </div>
                        </td>
                        {/* Rating */}
                        <td className="px-4 py-2 text-right">
                          <div className="flex justify-end">
                            <RatingBadge rating={loc.rating} />
                          </div>
                        </td>
                        {/* Views */}
                        <td className="hidden px-4 py-2 text-right lg:table-cell">
                          <div className="flex items-center justify-end gap-1">
                            <Eye className="text-muted-foreground size-3.5" aria-hidden />
                            <span className="text-muted-foreground text-sm">
                              {loc.profileViews.toLocaleString(i18n.locale)}
                            </span>
                          </div>
                        </td>
                        {/* 7-day mini chart */}
                        <td className="hidden px-4 py-2 md:table-cell">
                          <div className="flex justify-end">
                            <MiniBarChart data={loc.last7Days} />
                          </div>
                        </td>
                        {/* Quick link */}
                        <td className="px-2 py-2">
                          <Link
                            to="/account/facility/$facilityId/dashboard"
                            params={{ facilityId: loc.id }}
                            aria-label={t`Open dashboard: ${facilityName}`}
                            className="text-muted-foreground hover:text-primary inline-flex size-11 items-center justify-center rounded-lg transition-colors"
                          >
                            <ChevronRight className="size-4" aria-hidden />
                          </Link>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr id={detailId} className="border-border bg-muted/30 border-b lg:hidden">
                          <td colSpan={8} className="px-4 py-3">
                            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                              <dt className="text-muted-foreground">
                                <Trans>Appointments this month</Trans>
                              </dt>
                              <dd className="text-foreground text-right font-medium">
                                {loc.appointmentsMonth}
                              </dd>
                              <dt className="text-muted-foreground">{t`Doctors`}</dt>
                              <dd className="text-foreground text-right font-medium">
                                {loc.providerCount}
                              </dd>
                              <dt className="text-muted-foreground">{t`Views`}</dt>
                              <dd className="text-foreground text-right font-medium">
                                {loc.profileViews.toLocaleString(i18n.locale)}
                              </dd>
                              <dt className="text-muted-foreground">
                                <Trans>7 days</Trans>
                              </dt>
                              <dd className="flex justify-end">
                                <MiniBarChart data={loc.last7Days} />
                              </dd>
                            </dl>
                            <Link
                              to="/account/facility/$facilityId/dashboard"
                              params={{ facilityId: loc.id }}
                              className={cn(
                                buttonVariants({ variant: "outline", size: "touch" }),
                                "mt-3 w-full sm:w-auto",
                              )}
                            >
                              <Trans>Open dashboard</Trans>
                              <ChevronRight className="size-4" aria-hidden />
                            </Link>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bar chart: appointments per facility (this month) */}
      {!loading && sortedFacilities.length > 0 && (
        <div>
          <div className="mb-3 flex items-center gap-2">
            <BarChart3 className="text-muted-foreground size-4" aria-hidden />
            <h2 className="text-foreground text-sm font-semibold">
              <Trans>This Month</Trans>
            </h2>
          </div>
          <div className="border-border bg-card rounded-xl border p-4">
            <div className="space-y-3">
              {sortedFacilities.map((loc) => {
                const max = Math.max(...sortedFacilities.map((l) => l.appointmentsMonth), 1);
                const pct = Math.round((loc.appointmentsMonth / max) * 100);
                return (
                  <div key={loc.id} className="flex items-center gap-3">
                    <span className="text-muted-foreground w-32 shrink-0 truncate text-xs">
                      {loc.name}
                    </span>
                    <div
                      className="bg-muted flex-1 overflow-hidden rounded-full"
                      style={{ height: 8 }}
                    >
                      <div
                        className="bg-primary h-full rounded-full transition-all duration-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-foreground w-8 shrink-0 text-right text-xs font-semibold">
                      {loc.appointmentsMonth}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Quick links */}
      {!loading && sortedFacilities.length > 0 && (
        <div>
          <h2 className="text-foreground mb-3 text-sm font-semibold">
            <Trans>Locations</Trans>
          </h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {sortedFacilities.map((loc) => {
              const providerCount = loc.providerCount;
              const appointmentsToday = loc.appointmentsToday;
              return (
                <Link
                  key={loc.id}
                  to="/account/facility/$facilityId/dashboard"
                  params={{ facilityId: loc.id }}
                  className="border-border bg-card hover:border-primary/30 flex min-h-11 items-center gap-3 rounded-xl border p-3.5 transition-all hover:shadow-sm"
                >
                  <span className="bg-accent-soft flex size-9 shrink-0 items-center justify-center rounded-lg">
                    <Building2 className="text-accent-strong size-4" aria-hidden />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-foreground truncate text-sm font-medium">{loc.name}</p>
                    <p className="text-muted-foreground text-xs">
                      {t`${providerCount} doctors · ${appointmentsToday} today`}
                    </p>
                  </div>
                  <ChevronRight className="text-muted-foreground size-4 shrink-0" aria-hidden />
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <AccountSecurityBanner />
    </div>
  );
}
